export interface GridDay {
  date: Date;
  isCurrentMonth: boolean;
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * Returns a 2D grid (6 rows x 7 cols) of days for a given month.
 * Includes trailing days from the previous month and leading days from the next.
 */
export function getMonthGrid(year: number, month: number): GridDay[][] {
  const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const grid: GridDay[][] = [];
  let day = 1 - firstDay; // may be negative to include prev month

  for (let row = 0; row < 6; row++) {
    const week: GridDay[] = [];
    for (let col = 0; col < 7; col++) {
      const date = new Date(year, month, day);
      week.push({
        date,
        isCurrentMonth: day >= 1 && day <= daysInMonth,
      });
      day++;
    }
    grid.push(week);
  }

  return grid;
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function isToday(date: Date): boolean {
  return isSameDay(date, new Date());
}

export function formatMonthYear(year: number, month: number): string {
  return `${MONTH_NAMES[month]} ${year}`;
}

export const CATEGORY_COLORS: Record<string, string> = {
  monitoring_visit: "bg-indigo-500",
  data_review: "bg-amber-500",
  site_initiation: "bg-emerald-500",
  subject_visit: "bg-blue-500",
  default: "bg-slate-400",
};

export function getCategoryColor(category?: string): string {
  if (!category) return CATEGORY_COLORS.default;
  return CATEGORY_COLORS[category] ?? CATEGORY_COLORS.default;
}

/**
 * Parse a Firestore timestamp (may be serialized as { _seconds, _nanoseconds }
 * or an ISO string) into a JS Date.
 */
export function parseTimestamp(value: unknown): Date {
  if (!value) return new Date();
  if (typeof value === "string") return new Date(value);
  if (typeof value === "object" && value !== null && "_seconds" in value) {
    return new Date((value as { _seconds: number })._seconds * 1000);
  }
  return new Date();
}
